import { UserFindDto, type UserFindInterface } from '@zaoblako/shared/modules/security/dtos/user/user-find.dto';
import { EavAttributeValueTypeEnum } from '@zaoblako/shared/modules/eav/enums/eav-attribute-value-type.enum';
import { HelpdeskTaskStatusFindDto, type HelpdeskTaskStatusFindInterface } from '@zaoblako/shared/modules/helpdesk/dto/helpdesk-task-status-find.dto';
import { SerdeDtoClass } from '@zaoblako/shared/modules/serde/classes/serde-dto.class';
import { EavDtoKeysEnum } from '@zaoblako/shared/modules/eav/enums/eav-dto-keys.enum';
import { booleanSchema } from '@zaoblako/shared/modules/serde/schemas/boolean.schema';

import * as yup from 'yup';

/** EAV attribute response body - DTO signature */
export interface EavAttributeFindInterface {
  /** Unique ID */
  id: number;

  // Tech columns
  /** Creation date and time */
  created: Date;
  /** Last update date and time */
  updated: Date;
  /** Soft deletion update date and time. The EAV attribute will be permanently deleted six months after this date. */
  deleted?: Date;
  /** User who created this EAV attribute */
  createdBy?: UserFindInterface;

  // Data column

  /** EAV attribute display name */
  name: string;
  /** EAV attribute value type */
  type: EavAttributeValueTypeEnum;
  /**
   * If `true` then several value will be array of selected objects.
   * Using only when {@link EavAttributeFindInterface.type} equals:
   *   - {@link EavAttributeValueTypeEnum.FILIAL} (multiple filials selection);
   *   - {@link EavAttributeValueTypeEnum.COST_CENTER} (multiple cost centers selection);
   */
  multipleSelect?: boolean;
  /** Expression to compute. Using only when {@link EavAttributeFindInterface.type} equals {@link EavAttributeValueTypeEnum.COMPUTED}. */
  expression?: string;
  /**
   * HelpDesk task statuses where this attribute's value is required.
   * Using only for EAV entities related to HD tasks.
   */
  requiredOnHdTaskStatuses?: HelpdeskTaskStatusFindInterface[];
  /** If `true`, attribute is visible in client personal account HelpDesk */
  showInPersonalAccount?: boolean;
}

/** EAV attribute response body - DTO schema */
export const EavAttributeFindDto = SerdeDtoClass.create<EavAttributeFindInterface>(EavDtoKeysEnum.EAV_ATTRIBUTE_FIND_DTO, {
  id: {
    sortable: true,
    label: 'Номер',
    serializer: yup.number(),
  },
  created: {
    sortable: true,
    label: 'Создан',
    serializer: yup.date(),
  },
  updated: {
    sortable: true,
    label: 'Изменен',
    serializer: yup.date(),
  },
  deleted: {
    optional: true,
    label: 'Удален',
    serializer: yup.date(),
  },
  createdBy: {
    optional: true,
    label: 'Автор',
    nested: UserFindDto,
    serializer: (_, nested) => nested,
  },
  name: {
    label: 'Название',
    serializer: yup.string(),
  },
  type: {
    label: 'Тип',
    serializer: yup
      .string()
      .oneOf(Object.values(EavAttributeValueTypeEnum)),
  },
  multipleSelect: {
    optional: true,
    label: 'Множественный выбор',
    serializer: booleanSchema,
  },
  expression: {
    optional: true,
    label: 'Выражение для вычисляемого поля',
    serializer: yup.string(),
  },
  requiredOnHdTaskStatuses: {
    optional: true,
    label: 'Обязателен для заявок HD со статусами',
    nested: HelpdeskTaskStatusFindDto,
    serializer: (_, nested) => yup.array(nested),
  },
  showInPersonalAccount: {
    optional: true,
    label: 'Отражать в ЛК Клиента',
    serializer: booleanSchema,
  },
});
